import { Page } from "./Page"
import { throttle } from "lodash"

export type TimeSeriesPageRequest = {
	pageId: number
	studyId: string
	patientId: string
	modalities: string[]
	startDate: Date | number
	endDate: Date | number
	numPoints: number
	resolution: string
}

export class TimeSeriesPageQueue<PageType extends Page<any>> {
	public loadQueue: PageType[] = []
	public unloadQueue: PageType[] = []
	public onSuccess = (page: PageType) => {}
	public onPageLoadStart = (page: PageType) => {}
	public allLoadedPages = new Map<number, PageType>()

	public activePageRequests = new Map<number, PageType>()
	private queueGeneration = 0
	private maxConcurrentRequests: number
	private activeRequestStartedAt = new Map<number, number>()
	private activeRequestTokens = new Map<number, number>()
	private timedOutRequestRetries = new Map<number, number>()
	private requestSequence = 0
	private activeRequestTimeoutMs = 10000
	private maxTimedOutRequestRetries = 2
	private lastQueueDebugLogTime = 0
	private queueDebugCounters = {
		pushed: 0,
		queued: 0,
		skipped: 0,
		started: 0,
		partial: 0,
		finished: 0,
		stalePartial: 0,
		staleFinish: 0,
		requeued: 0,
		replaced: 0,
		loadErrors: 0,
		timedOut: 0,
		retried: 0,
		abandoned: 0,
		canceled: 0,
		cleared: 0,
		unloaded: 0
	}

	constructor(maxConcurrentRequests: number = 1) {
		this.maxConcurrentRequests = Math.max(1, maxConcurrentRequests)
	}

	private logQueueEvent = (event: string, page?: PageType, extra: Record<string, unknown> = {}, force: boolean = false) => {
		if (window.localStorage.getItem("MCP_EEG_WPS_DEBUG") !== "true") {
			return
		}

		const now = performance.now()

		if (!force && now - this.lastQueueDebugLogTime < 1000) {
			return
		}

		this.lastQueueDebugLogTime = now

		console.log("[TimeSeriesPageQueue]", JSON.stringify({
			event,
			pageId: page?.id,
			pageType: page?.getType(),
			startTime: page ? new Date(page.startTime).toISOString() : undefined,
			endTime: page ? new Date(page.endTime).toISOString() : undefined,
			active: this.activePageRequests.size,
			queued: this.loadQueue.length,
			loaded: this.allLoadedPages.size,
			maxConcurrentRequests: this.maxConcurrentRequests,
			generation: this.queueGeneration,
			activePageIds: [...this.activePageRequests.keys()],
			nextQueuedPageIds: this.loadQueue.slice(0, 8).map(queuedPage => queuedPage.id),
			counters: this.queueDebugCounters,
			...(extra ?? {})
		}, null, 2))
	}

	getLoadingMessage = () => {
		if (this.activePageRequests.size === 0) {
			return "Finished Loading."
		}

		return [...this.activePageRequests.values()]
			.map(page => `${page.id} (${new Date(page.startTime)} to ${new Date(page.endTime)}) - ${page.loadingMessage}`)
			.join("; ")
	}

	push = (pages: PageType[]) => {
		const nonDuplicates = pages.filter(page => !page.loading && !this.loadQueue.find(p => page.id === p.id) && !this.activePageRequests.has(page.id))
		this.loadQueue.push(...nonDuplicates)
		this.queueDebugCounters.pushed += pages.length
		this.queueDebugCounters.queued += nonDuplicates.length
		this.queueDebugCounters.skipped += pages.length - nonDuplicates.length
		this.logQueueEvent("push", undefined, {
			requestedCount: pages.length,
			queuedCount: nonDuplicates.length,
			firstRequestedPageIds: pages.slice(0, 8).map(page => page.id),
			firstQueuedPageIds: nonDuplicates.slice(0, 8).map(page => page.id),
			skippedDuplicateCount: pages.length - nonDuplicates.length
		})
		this.drain()
	}

	replace = (pages: PageType[]) => {
		const nextLoadQueue = pages.filter(page => !page.loading && !this.activePageRequests.has(page.id))

		this.queueDebugCounters.pushed += pages.length
		this.queueDebugCounters.queued += nextLoadQueue.length
		this.queueDebugCounters.replaced += 1
		this.queueDebugCounters.skipped += pages.length - nextLoadQueue.length
		this.loadQueue = nextLoadQueue

		this.logQueueEvent("replace", undefined, {
			requestedCount: pages.length,
			queuedCount: nextLoadQueue.length,
			firstRequestedPageIds: pages.slice(0, 8).map(page => page.id),
			firstQueuedPageIds: nextLoadQueue.slice(0, 8).map(page => page.id),
			skippedActiveOrLoadingCount: pages.length - nextLoadQueue.length
		})

		this.drain()
	}

	preemptActiveRequestsBefore = (startTime: number, minimumActiveDurationMs: number = 1000) => {
		const now = performance.now()
		const canceledPageIds: number[] = []

		for (const page of [...this.activePageRequests.values()]) {
			if (page.startTime >= startTime) {
				continue
			}

			const startedAt = this.activeRequestStartedAt.get(page.id)

			if (startedAt === undefined || now - startedAt < minimumActiveDurationMs) {
				continue
			}

			this.activePageRequests.delete(page.id)
			this.activeRequestStartedAt.delete(page.id)
			this.activeRequestTokens.delete(page.id)
			canceledPageIds.push(page.id)
			page.cancel()
		}

		this.queueDebugCounters.canceled += canceledPageIds.length

		if (canceledPageIds.length > 0) {
			this.logQueueEvent("preempt-active-before", undefined, {
				canceledPageIds,
				startTime: new Date(startTime).toISOString(),
				minimumActiveDurationMs
			}, true)
		}
	}

	unloadAllLoadedPages() {
		this.queueDebugCounters.unloaded += this.allLoadedPages.size
		this.logQueueEvent("unload-all-loaded", undefined, undefined, true)
		this.allLoadedPages.forEach(page => page.unload())
	}

	unloadOutside = throttle((neighborhood: PageType[]) => {
		const neighborhoodPages = new Set(neighborhood)
		const queuedBefore = this.loadQueue.length

		this.loadQueue = this.loadQueue.filter(page => neighborhoodPages.has(page))
		const removedQueuedCount = queuedBefore - this.loadQueue.length

		this.unloadQueue.length = 0

		for (const page of this.allLoadedPages.values()) {
			if (!neighborhoodPages.has(page)) {
				this.unloadQueue.push(page)
			}
		}

		for (const page of this.unloadQueue) {
			this.allLoadedPages.delete(page.id)
			page.unload()
		}

		const canceledActivePageIds: number[] = []

		for (const page of [...this.activePageRequests.values()]) {
			if (!neighborhoodPages.has(page)) {
				this.activePageRequests.delete(page.id)
				this.activeRequestStartedAt.delete(page.id)
				this.activeRequestTokens.delete(page.id)
				canceledActivePageIds.push(page.id)
				page.cancel()
			}
		}

		this.queueDebugCounters.canceled += canceledActivePageIds.length
		this.queueDebugCounters.unloaded += this.unloadQueue.length

		this.logQueueEvent("unload-outside", undefined, {
			neighborhoodCount: neighborhood.length,
			firstNeighborhoodPageIds: neighborhood.slice(0, 8).map(page => page.id),
			removedQueuedCount,
			unloadedCount: this.unloadQueue.length,
			firstUnloadedPageIds: this.unloadQueue.slice(0, 8).map(page => page.id),
			canceledActivePageIds
		}, true)

		this.drain()
	}, 500)

	drain = () => {
		this.cancelExpiredActiveRequests()

		for (const page of [...this.activePageRequests.values()]) {
			if (page.needsToBeLoaded()) {
				this.activePageRequests.delete(page.id)
				this.activeRequestStartedAt.delete(page.id)
				this.activeRequestTokens.delete(page.id)
				this.loadQueue.unshift(page)
				this.queueDebugCounters.requeued += 1
				this.logQueueEvent("requeue-not-loading", page, undefined, true)
			}
		}

		while (this.loadQueue.length > 0 && this.activePageRequests.size < this.maxConcurrentRequests) {
			const pageRequest = this.loadQueue.shift()

			if (!pageRequest) {
				return
			}

			this.startPageRequest(pageRequest, this.queueGeneration)
		}
	}

	pop = this.drain

	private startPageRequest = (pageRequest: PageType, generation: number) => {
		if (this.activePageRequests.has(pageRequest.id)) {
			return
		}

		this.activePageRequests.set(pageRequest.id, pageRequest)
		this.activeRequestStartedAt.set(pageRequest.id, performance.now())
		const requestToken = ++this.requestSequence
		this.activeRequestTokens.set(pageRequest.id, requestToken)
		this.queueDebugCounters.started += 1
		this.logQueueEvent("start", pageRequest)

		pageRequest.setOnPartialLoad((loadedPage: Page<any>) => {
			if (!this.isCurrentRequest(loadedPage as PageType, generation, requestToken)) {
				this.queueDebugCounters.stalePartial += 1
				this.logQueueEvent("ignore-stale-partial", loadedPage as PageType, { callbackGeneration: generation }, true)
				return
			}

			this.onSuccess(loadedPage as PageType)
			this.allLoadedPages.set(loadedPage.id, loadedPage as PageType)
			this.queueDebugCounters.partial += 1
			this.logQueueEvent("partial", loadedPage as PageType)
		})

		pageRequest.setOnFinishedLoading((loadedPage: Page<any>) => {
			if (!this.isCurrentRequest(loadedPage as PageType, generation, requestToken)) {
				this.queueDebugCounters.staleFinish += 1
				this.logQueueEvent("ignore-stale-finish", loadedPage as PageType, { callbackGeneration: generation }, true)
				return
			}

			this.onSuccess(loadedPage as PageType)
			this.allLoadedPages.set(loadedPage.id, loadedPage as PageType)
			this.activePageRequests.delete(loadedPage.id)
			this.activeRequestStartedAt.delete(loadedPage.id)
			this.activeRequestTokens.delete(loadedPage.id)
			this.timedOutRequestRetries.delete(loadedPage.id)
			this.queueDebugCounters.finished += 1
			this.logQueueEvent("finish", loadedPage as PageType)
			this.drain()
		})

		try {
			pageRequest.load()
			this.onPageLoadStart(pageRequest)
		} catch (error) {
			console.error(error)
			pageRequest.unload()
			this.activePageRequests.delete(pageRequest.id)
			this.activeRequestStartedAt.delete(pageRequest.id)
			this.activeRequestTokens.delete(pageRequest.id)
			this.queueDebugCounters.loadErrors += 1
			this.logQueueEvent("load-error", pageRequest, { error }, true)
			this.drain()
		}
	}

	private cancelExpiredActiveRequests = () => {
		const now = performance.now()

		for (const page of [...this.activePageRequests.values()]) {
			const startedAt = this.activeRequestStartedAt.get(page.id)

			if (startedAt === undefined || now - startedAt <= this.activeRequestTimeoutMs) {
				continue
			}

			const retryCount = (this.timedOutRequestRetries.get(page.id) ?? 0) + 1

			this.activePageRequests.delete(page.id)
			this.activeRequestStartedAt.delete(page.id)
			this.activeRequestTokens.delete(page.id)
			page.cancel()
			this.timedOutRequestRetries.set(page.id, retryCount)
			this.queueDebugCounters.timedOut += 1

			if (retryCount <= this.maxTimedOutRequestRetries) {
				this.loadQueue.unshift(page)
				this.queueDebugCounters.retried += 1
				this.logQueueEvent("timeout-retry", page, { retryCount }, true)
			} else {
				this.queueDebugCounters.abandoned += 1
				this.logQueueEvent("timeout-abandon", page, { retryCount }, true)
			}
		}
	}

	private isCurrentRequest = (page: PageType, generation: number, requestToken: number) => {
		return generation === this.queueGeneration && this.activePageRequests.get(page.id) === page && this.activeRequestTokens.get(page.id) === requestToken
	}

	cancel = () => {
		this.queueGeneration += 1
		const canceledPageIds = [...this.activePageRequests.keys()]
		this.queueDebugCounters.canceled += canceledPageIds.length
		for (const page of [...this.activePageRequests.values()]) {
			page.cancel()
		}
		this.activePageRequests.clear()
		this.activeRequestStartedAt.clear()
		this.activeRequestTokens.clear()
		this.clear()
		this.logQueueEvent("cancel", undefined, { canceledPageIds }, true)
	}

	clear = () => {
		if (this.loadQueue.length > 0) {
			this.queueDebugCounters.cleared += this.loadQueue.length
			this.logQueueEvent("clear", undefined, {
				clearedCount: this.loadQueue.length,
				firstClearedPageIds: this.loadQueue.slice(0, 8).map(page => page.id)
			}, true)
		}

		this.loadQueue.length = 0
	}
}
